import { Component, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { navigationRoutes } from '../../routes/navigation/navigation-routes';
import { interval, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { OtpVerificationService } from './otp-verification.service';
import { OtpVerifyResponse } from '../../models/otp-verify-response';
import { Status } from '../../models/response';
import { SentOtpBody } from '../../models/sent-otp-body';
import { UiUtilityService } from '../../services/ui-utility/ui-utility.service';
import { SendOtpResponse } from '../../models/send-otp-response';
import { environment } from '../../../../environments/environment';
import { OtpVerifyBody } from '../../models/otp-verify-body';

declare global {
    interface Window {
        grecaptcha?: {
            ready(arg0: () => void): unknown;
            render: (containerId: string, options: { sitekey: string; callback: (token: string) => void }) => void;
        } & Partial<{
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
        }>;
    }
}

@Component({
    selector: 'app-otp-verification',
    standalone: false,
    templateUrl: './otp-verification.component.html',
    styleUrl: './otp-verification.component.scss',
})
export class OtpVerificationComponent implements OnInit, OnDestroy {
    otpVerificationForm: FormGroup;
    navigationRoutes = navigationRoutes;
    returnUrl!: string;
    isResendDisabled = false;
    remainingTime!: number;
    resendIntervalSeconds = 1;
    timerSubscription!: Subscription;
    otpPurpose!:
        | 'login'
        | 'resetPassword'
        | 'new-user-registration'
        | 'partnerProgramLogin'
        | 'partnerProgramRegistration'
        | 'partnerProgramResetPassword';
    flowfor?: string;
    captchaToken: string | null = null;
    isCaptchaRequired = false;
    isCaptchaRendered = false; // NEW field

    @Input() flowFrom?: string;

    constructor(
        private readonly fb: FormBuilder,
        private readonly otpService: OtpVerificationService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly zone: NgZone,
        private readonly router: Router,
        private readonly uiUtilityService: UiUtilityService,
    ) {
        window.addEventListener('captcha-success', (event: Event) => {
            this.captchaToken = (event as CustomEvent).detail;
        });

        this.otpVerificationForm = this.fb.group({
            otp: ['', Validators.required],
        });
    }

    onCaptchaResolved(token: string) {
        this.captchaToken = token;
    }

    ngOnInit(): void {
        this.otpPurpose = sessionStorage.getItem('otp-purpose') as
            | 'login'
            | 'resetPassword'
            | 'new-user-registration'
            | 'partnerProgramLogin'
            | 'partnerProgramRegistration'
            | 'partnerProgramResetPassword';
        this.returnUrl = this.activatedRoute.snapshot.queryParams['returnUrl'] || '/ofglobaltestapp';
        this.flowfor = this.activatedRoute.snapshot.queryParams['flowFor'];

        if (this.flowfor === 'login' && this.flowFrom !== 'partnerProgram') {
            this.processLogin();
        } else {
            this.startTimer();
        }

        // harcoded for now
        this.flowfor = 'emailOtp';
    }

    startTimer(): void {
        this.timerSubscription?.unsubscribe();
        this.remainingTime = this.resendIntervalSeconds;
        this.isResendDisabled = true;
        this.timerSubscription = interval(1000).subscribe(() => {
            this.zone.run(() => {
                if (--this.remainingTime <= 0) {
                    this.timerSubscription.unsubscribe();
                    this.isResendDisabled = false;
                }
            });
        });
    }

    getFormattedTime(seconds: number): string {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        const formattedMinutes = String(minutes).padStart(2, '0');
        const formattedSeconds = String(remainingSeconds).padStart(2, '0');
        return `${formattedMinutes}:${formattedSeconds}`;
    }

    resendOTP() {
        const sendOtpBody: SentOtpBody = {
            email: sessionStorage.getItem('email-id'),
            id: sessionStorage.getItem('otp-user-id'),
            event: sessionStorage.getItem('otp-event'),
            captchaToken: null,
        };

        // If captcha is required, send captchaToken also
        if (this.isCaptchaRequired && this.captchaToken) {
            sendOtpBody.captchaToken = this.captchaToken;
        }

        if (this.isCaptchaRequired && !this.captchaToken) {
            // If captcha is required but not selected, show alert and prevent submit
            this.uiUtilityService.handleError('Please complete the captcha before submitting.');
            return;
        }

        this.otpService.resendOtp(sendOtpBody).subscribe({
            next: (res: SendOtpResponse) => {
                if (res?.status === Status.OK) {
                    this.isCaptchaRequired = false;
                    this.isCaptchaRendered = false; // ✅ Reset render flag
                    this.startTimer();
                } else if (res?.captchaRequired) {
                    this.isCaptchaRequired = true;
                    this.isResendDisabled = true;
                    this.executeCaptcha();
                } else if (res?.tooManyRequests) {
                    this.isCaptchaRequired = false;
                    this.isCaptchaRendered = false; // ✅ Reset render flag
                    this.isResendDisabled = true;
                }
            },
            error: () => {
                this.isResendDisabled = true;
            },
        });
    }

    private executeCaptcha(): void {
        const grecaptcha = window['grecaptcha'];

        if (grecaptcha?.ready && typeof grecaptcha.ready === 'function') {
            grecaptcha.ready(() => {
                grecaptcha.execute?.(environment.googleSiteKey, { action: 'otp_verification' })?.then((token: string) => {
                    this.zone.run(() => {
                        this.captchaToken = token;
                        this.isResendDisabled = false;
                        this.startTimer(); // resume timer
                    });
                });
            });

            this.isCaptchaRendered = true;
        } else {
            console.error('reCAPTCHA is not available.');
        }
    }

    submitForm(): void {
        if (this.isCaptchaRequired && !this.captchaToken) {
            // If captcha is required but not selected, show alert and prevent submit
            this.uiUtilityService.handleError('Please complete the captcha before submitting.');
            return;
        }

        const otpVerifyBody: OtpVerifyBody = {
            id: sessionStorage.getItem('otp-user-id'),
            event: sessionStorage.getItem('otp-event'),
            email: sessionStorage.getItem('otp-email'),
            tempOtp: this.otpVerificationForm.value.otp,
            mfaMethod: 'email',
            captchaToken: this.captchaToken, // pass token here
        };
        if (this.otpVerificationForm.valid) {
            this.otpService.verifyOtp(otpVerifyBody, this.flowFrom).subscribe((res: OtpVerifyResponse) => {
                if (res.status === Status.OK && res.verificationStatus) {
                    this.processLogin();
                }
            });
        }
    }

    processLogin(): void {
        if (this.flowFrom === 'partnerProgram') {
            console.log('this.otpPurpose 1', this.otpPurpose);
            localStorage.setItem('login-flow', 'partner-program');
            this.otpService.processLogin(this.otpPurpose, this.returnUrl ?? '/ofglobaltestapp');
        }

        if (this.flowFrom === 'partnerProgramAdmin') {
            console.log('this.otpPurpose 1', this.otpPurpose);
            localStorage.setItem('login-flow', 'admin');
            this.otpService.processLogin(this.otpPurpose, this.returnUrl ?? '/admin-login');
        }
    }

    backFromOtpVerificationScreen(): void {
        this.router.navigate([navigationRoutes.navigateToPartnerProgramLogin]);
    }

    ngOnDestroy() {
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
        }
    }
}
